import {
  inject,
  Injectable,
} from '@angular/core';
import {Router} from '@angular/router';
import {lastValueFrom} from 'rxjs';
import {HttpStatusCode} from '@angular/common/http';
import {JwtService} from '../auth/jwt/jwt.service';
import {ApiSharedLinkService} from '../api/common-feature/api-shared-link/api-shared-link.service';
import {AppTranslateService} from '../page/i18n/translate/app-translate/app-translate.service';
import {ErrorService} from '../page/error/error/error.service';
import {Constants} from '../../../common/constants/constants';
import {UrlFunctions} from '../../../common/functions/url';
import {Links} from '../../../common/constants/links';
import {AlertService} from '../../../modules/alert/services/alert/alert.service';
import {SharedLinkSource} from '../../../common/enums/shared-link-source.enum';

export interface ISharedLink {
  id?: string;
  payload?: string;
  source?: string;
  createdAt?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SharedLinkService {

  private jwtService = inject(JwtService);

  private apiSharedLinkService = inject(ApiSharedLinkService);

  private appTranslateService = inject(AppTranslateService);

  private alertService = inject(AlertService);

  private errorService = inject(ErrorService);

  private router = inject(Router);

  private tokenValue: any = null;

  get token(): any {
    return this.tokenValue;
  }

  set token(value: any) {
    this.tokenValue = value;
  }

  /**
   * Method: Check shared link pages
   */
  isSharedLinkPage(): boolean {
    return UrlFunctions.isPageByRouteUrl(Links.SHARED_LINK_URL);
  }

  /**
   * Method: Get hash for shared link
   */
  getSharedLinkId(): string {
    const URLObj = new URL(window.location.href);
    const pathNameArr = URLObj.pathname.split(Constants.SLASH);
    return pathNameArr[pathNameArr.length - 1];
  }

  /**
   * Method: Auth by shared link hash
   */
  sharedLinkAuth(): any {
    return this.apiSharedLinkService.sharedLinkAuth().subscribe(
      {
        next: response => {
          this.token = response.token;
          this.jwtService.setGuestToken(response.token);
        },
        error: () => {
          this.alertService.error(this.appTranslateService.getTranslation('sng.error.access_denied'));
        },
      },
    );
  }

  /**
   * Method: Create a shared link for the payload and return its id
   * @param payload - Serialized data the shared link opens
   * @param source - Feature the shared link belongs to
   */
  public async createSharedLinkData(payload: string, source: SharedLinkSource): Promise<string | null> {
    try {
      const result = await lastValueFrom(
        this.apiSharedLinkService.createSharedLinkDataObservable(payload, source),
      );
      this.errorService.showResponseErrors(result);
      return result?.data?.createSharedLink?.result ?? null;
    } catch (err) {
      this.errorService.showGraphQLErrors(err);
      return null;
    }
  }

  /**
   * Method: Get the shared link of the current url
   */
  async getSharedLinkData(): Promise<ISharedLink | null> {
    try {
      const result = await lastValueFrom(this.apiSharedLinkService.getSharedLinkDataObservable(this.getSharedLinkId()));
      this.errorService.showResponseErrors(result);
      this.detectErrorSharedLink(result);
      return result?.data?.sharedLink ?? null;
    } catch (err) {
      this.errorService.showGraphQLErrors(err);
      return null;
    }
  }

  /**
   * Method: Detect error for shared link page
   */
  detectErrorSharedLink(response: any): void {
    const errors = this.errorService.getResponseErrors(response);
    if (errors?.length) {
      errors.forEach(async error => {
        const statusString = error?.extensions?.status as string;
        const statusCode = parseInt(statusString, 10);
        if (statusCode === HttpStatusCode.Forbidden) {
          await this.router.navigate([Links.ERROR_LINK_URL, Links.ACCESS_DENIED_LINK_URL]);
        } else if (statusCode === HttpStatusCode.NotFound) {
          await this.router.navigate([Links.ERROR_LINK_URL, HttpStatusCode.NotFound]);
        }
      });
    }
  }

}
